import * as Sentry from '@sentry/react';

const ANNOUNCE_EVENT = 'eip6963:announceProvider';

type AnnounceDetail = {
  info?: { uuid?: unknown; name?: unknown; rdns?: unknown };
  provider?: { request?: unknown };
};

const reported = new Set<string>();

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

function findViolation(detail: unknown): string | undefined {
  if (!detail || typeof detail !== 'object') return 'detail is not an object';

  const { info, provider } = detail as AnnounceDetail;
  if (!info || typeof info !== 'object') return 'info is missing';

  // Exactly the fields mipd, wagmi and RainbowKit dereference unchecked.
  if (!isNonEmptyString(info.uuid)) return 'info.uuid is not a string';
  if (!isNonEmptyString(info.rdns)) return 'info.rdns is not a string';
  if (!isNonEmptyString(info.name)) return 'info.name is not a string';
  if (!provider || typeof provider.request !== 'function') return 'provider.request is missing';

  return undefined;
}

/** `isRabby` / `isPhantom`-style flags are the only handle on an announcer that skipped `info`. */
function describeProvider(detail: unknown): string[] | undefined {
  const provider = (detail as AnnounceDetail | null)?.provider;
  if (!provider || typeof provider !== 'object') return undefined;
  try {
    return Object.keys(provider).slice(0, 30);
  } catch {
    return undefined;
  }
}

/** Announcements repeat on every provider request, so report each offender once. */
function report(violation: string, detail: unknown): void {
  const info = (detail as AnnounceDetail | null)?.info;
  const identity = [info?.rdns, info?.name].filter(isNonEmptyString).join(' ') || 'unidentified';
  const key = `${violation}|${identity}`;
  if (reported.has(key)) return;
  reported.add(key);

  console.warn(`[Perpl] Ignoring a malformed EIP-6963 announcement from ${identity}: ${violation}`);
  Sentry.captureMessage('Malformed EIP-6963 provider announcement', {
    level: 'warning',
    extra: { violation, identity, providerKeys: describeProvider(detail) },
  });
}

/**
 * `mipd` stores whatever `detail` an extension announces, and wagmi reads `detail.info.rdns` off
 * it — in `createConfig()` and again on every later announcement — so one extension announcing
 * without `info` takes the whole app down.
 *
 * mipd's store is created inside `createConfig()`, so a listener installed before the first
 * `createConfig()` call runs ahead of it (same target, so registration order wins) and keeps
 * malformed announcements out of the store, which covers every read site at once.
 */
export function installEip6963Guard(): () => void {
  if (typeof window === 'undefined') return () => {};

  const handler = (event: Event) => {
    const { detail } = event as CustomEvent<unknown>;
    const violation = findViolation(detail);
    if (!violation) return;

    event.stopImmediatePropagation();
    report(violation, detail);
  };

  window.addEventListener(ANNOUNCE_EVENT, handler, { capture: true });

  return () => {
    window.removeEventListener(ANNOUNCE_EVENT, handler, { capture: true });
    reported.clear();
  };
}
